import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtHours } from "@/lib/calc";
import { Trash2, Plus, Clock, Sparkles, MessageSquare, FilePlus } from "lucide-react";
import { toast } from "sonner";

const QUICK_AMOUNTS = [0.5, 1, 2, 5];

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    if (isToday) return "Today";
    if (isYesterday) return "Yesterday";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const IconForType = ({ type }) => {
  const cls = "w-3.5 h-3.5";
  if (type === "hours") return <Clock className={cls + " text-[#B89B5E]"} />;
  if (type === "note") return <MessageSquare className={cls + " text-[#69747C]"} />;
  if (type === "created") return <FilePlus className={cls + " text-[#42624B]"} />;
  return <Sparkles className={cls + " text-[#69747C]"} />;
};

const ActivityTimeline = ({ clientId, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customHours, setCustomHours] = useState("");
  const [hoursNote, setHoursNote] = useState("");
  const [hoursDate, setHoursDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [noteBody, setNoteBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}/activity`);
      setItems(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[activity-timeline] failed to load", e?.response?.status || e?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const addHours = async (hours) => {
    if (!hours || hours <= 0) {
      toast.error("Enter hours greater than 0");
      return;
    }
    setSaving(true);
    try {
      // hoursDate is yyyy-mm-dd; convert to ISO timestamp at noon UTC of that date
      // so it sorts correctly and shows the picked day in any timezone.
      const dateISO = hoursDate
        ? new Date(`${hoursDate}T12:00:00Z`).toISOString()
        : new Date().toISOString();
      await api.post(`/clients/${clientId}/hours`, {
        hours: Number(hours),
        note: hoursNote || "",
        date: dateISO,
      });
      toast.success(`+${hours}h logged`);
      setHoursNote("");
      setCustomHours("");
      setHoursDate(new Date().toISOString().slice(0, 10));
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log hours");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) {
      toast.error("Note can not be empty");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/notes`, { body: noteBody.trim() });
      toast.success("Note added");
      setNoteBody("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (item) => {
    try {
      if (item.type === "hours") {
        await api.delete(`/clients/${clientId}/hours/${item.id}`);
      } else if (item.type === "note") {
        await api.delete(`/clients/${clientId}/notes/${item.id}`);
      } else {
        return;
      }
      toast.success("Removed");
      await load();
      onChange?.();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div
      className="bg-white border border-[#E4DFD2] rounded-xl p-6"
      data-testid="activity-timeline"
    >
      <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
        Activity
      </div>
      <div className="font-display text-lg font-semibold text-[#1F2326]">
        Hours and notes
      </div>

      {/* Quick-add hours */}
      <div className="mt-5">
        <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C] mb-2">
          Log hours
        </div>
        <div className="flex flex-wrap gap-2">
          {QUICK_AMOUNTS.map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => addHours(h)}
              disabled={saving}
              data-testid={`quick-add-${h}`}
              className="px-3 py-1.5 rounded-lg border border-[#E4DFD2] bg-white hover:bg-[#FBFAF6] hover:border-[#D6CFB9] disabled:opacity-50 text-sm font-medium text-[#1F2326] transition-colors inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> {h}h
            </button>
          ))}
          <div className="flex items-center gap-2 ml-1">
            <input
              data-testid="custom-hours-input"
              type="number"
              step="0.25"
              min="0"
              value={customHours}
              onChange={(e) => setCustomHours(e.target.value)}
              placeholder="custom"
              className="w-20 px-2.5 py-1.5 bg-white border border-[#E4DFD2] rounded-lg text-sm focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326]"
            />
            <button
              type="button"
              onClick={() => addHours(parseFloat(customHours))}
              disabled={saving || !customHours}
              data-testid="custom-hours-add-btn"
              className="px-3 py-1.5 rounded-lg bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] disabled:opacity-50 text-sm font-medium"
            >
              Add
            </button>
          </div>
        </div>
        <input
          data-testid="hours-note-input"
          type="text"
          value={hoursNote}
          onChange={(e) => setHoursNote(e.target.value)}
          placeholder="Optional note for the hours"
          className="w-full mt-2 px-3 py-2 bg-white border border-[#E4DFD2] rounded-lg text-sm placeholder-[#A9A294] focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326]"
        />
        <div className="flex items-center gap-2 mt-2">
          <label className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
            On
          </label>
          <input
            data-testid="hours-date-input"
            type="date"
            value={hoursDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setHoursDate(e.target.value)}
            className="px-2.5 py-1.5 bg-white border border-[#E4DFD2] rounded-lg text-sm focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326]"
          />
          <button
            type="button"
            onClick={() => setHoursDate(new Date().toISOString().slice(0, 10))}
            className="text-xs text-[#69747C] hover:text-[#1F2326] px-1"
            data-testid="hours-date-today-btn"
          >
            Today
          </button>
        </div>
      </div>

      {/* Add note */}
      <div className="mt-5 pt-5 border-t border-[#EDE7D6]">
        <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C] mb-2">
          Add a note
        </div>
        <div className="flex gap-2">
          <input
            data-testid="note-input"
            type="text"
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }}
            placeholder="Scope change, client request, anything to remember."
            className="flex-1 px-3 py-2 bg-white border border-[#E4DFD2] rounded-lg text-sm placeholder-[#A9A294] focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326]"
          />
          <button
            type="button"
            onClick={addNote}
            disabled={saving || !noteBody.trim()}
            data-testid="add-note-btn"
            className="px-3 py-2 rounded-lg bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] disabled:opacity-50 text-sm font-medium"
          >
            Add note
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-6 pt-5 border-t border-[#EDE7D6]">
        <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C] mb-3">
          Timeline
        </div>
        {loading ? (
          <div className="text-sm text-[#69747C] py-3">Loading.</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[#69747C] py-3">No activity yet.</div>
        ) : (
          <ul className="space-y-2" data-testid="activity-list">
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`activity-item-${it.id}`}
                className="group flex items-start gap-3 rounded-lg border border-[#EDE7D6] px-3 py-2.5"
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-none bg-[#F0EADA]">
                  <IconForType type={it.type} />
                </div>
                <div className="min-w-0 flex-1">
                  {it.type === "hours" ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-display text-sm font-semibold text-[#1F2326] tabular-nums">
                          +{fmtHours(it.hours)} logged
                        </span>
                        <span className="text-xs text-[#69747C]">{formatWhen(it.created_at)}</span>
                      </div>
                      {it.note ? (
                        <div className="text-xs text-[#69747C] mt-0.5">{it.note}</div>
                      ) : null}
                    </div>
                  ) : it.type === "note" ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-medium text-[#1F2326]">Note</span>
                        <span className="text-xs text-[#69747C]">{formatWhen(it.created_at)}</span>
                      </div>
                      <div className="text-sm text-[#1F2326] mt-0.5">{it.body}</div>
                    </div>
                  ) : it.type === "created" ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-medium text-[#1F2326]">Retainer added</span>
                        <span className="text-xs text-[#69747C]">{formatWhen(it.created_at)}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
                {(it.type === "hours" || it.type === "note") ? (
                  <button
                    type="button"
                    onClick={() => deleteItem(it)}
                    data-testid={`activity-delete-${it.id}`}
                    className="opacity-0 group-hover:opacity-100 text-[#A9A294] hover:text-[#C76055] transition-all"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;
